import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { previewFork, type ForkChoice, type ForkPreview } from "../../../api/threads/threadsApi";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { NO_STEP_NAMES, stepNamesOf } from "../data/stepNames";
import { useThreadProcess } from "../data/useThreadProcess";
import { previewStepName, stepOutcome } from "./forkVersions";
import KeptInputNoteLine from "./KeptInputNoteLine";

/**
 * What the fork would keep and run (D37), from the server's own fork plan, so the preview
 * and the fork cannot differ: the steps that keep their results, the one that runs from the
 * fork point, and a quiet note on a kept step that read an item which has a newer version now:
 * by the item's name, with the exact versions in the tooltip only.
 */
export default function ForkPreviewList({ threadId, projectId, choice }: { threadId: string; projectId: string; choice: ForkChoice }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => previewFork(threadId, choice, signal), [threadId, choice]);
  const { state } = useLoad(load);
  if (state.status !== "loaded") {
    return <LoadStatus state={state} loadingLabel={t("threads.fork.preview.loading")} compact={true} />;
  }
  return state.value.steps.length === 0 ? null : <PreviewSteps preview={state.value} projectId={projectId} />;
}

/** The steps, named by the fork's own Workflow version, not the Thread's (R3-02). */
function PreviewSteps({ preview, projectId }: { preview: ForkPreview; projectId: string }) {
  const { t, i18n } = useTranslation();
  const { state: process } = useThreadProcess(preview.workflow, projectId);
  if (process.status === "loading") {
    return <LoadStatus state={process} loadingLabel={t("threads.fork.preview.loading")} />;
  }
  const names = process.status === "loaded" && process.value !== null ? stepNamesOf(process.value, i18n.resolvedLanguage) : NO_STEP_NAMES;
  return (
    <section className="space-y-1 text-sm">
      <h3 className="app-title font-semibold">{t("threads.fork.preview.title")}</h3>
      <ol className="space-y-1">
        {preview.steps.map((step) => (
          <li key={step.stepKey}>
            <span>{previewStepName(names, step) ?? t("threads.fork.preview.unnamedStep")}</span>
            <span className="app-text-muted"> · {t(`threads.fork.preview.${stepOutcome(preview, step)}`)}</span>
            {preview.notes
              .filter((note) => note.stepKey === step.stepKey)
              .map((note) => (
                <KeptInputNoteLine key={`${note.input}:${note.read}`} note={note} className={`block ${FORM_HELP_TEXT_CLASS}`} />
              ))}
          </li>
        ))}
      </ol>
    </section>
  );
}
